import {
  Alert,
  Box,
  Heading,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';

import {
  CreditFacilityGQLType,
  LoanProductGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { useImplementationDetailsQuery } from './implementationDetails.gql.generated';
import { colors } from 'packages/common-frontend/colors';

// Types
const RouteParamsStruct = z.object({
  implementationId: z.string({ message: 'Missing implementationId' }),
});

// Helpers
type LoanProductsTableProps = {
  loanProducts: Pick<LoanProductGQLType, 'id' | 'name' | 'slug'>[];
};

const LoanProductsTable = ({ loanProducts }: LoanProductsTableProps) => {
  return (
    <Box mb="36px">
      <Heading size="md" mb="24px">
        Loan Products
      </Heading>
      <Table>
        <Thead>
          <Tr>
            <Th>Id</Th>
            <Th>Name</Th>
            <Th>Slug</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loanProducts.map((loanProduct) => (
            <Tr key={loanProduct.id}>
              <Td>{loanProduct.id}</Td>
              <Td>{loanProduct.name}</Td>
              <Td>{loanProduct.slug}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {loanProducts.length === 0 && (
        <Alert> No loan products are defined.</Alert>
      )}
    </Box>
  );
};

type CreditFacilityTableProps = {
  creditFacilities: Pick<
    CreditFacilityGQLType,
    'id' | 'name' | 'slug' | 'borrowerEntityName'
  >[];
};
const CreditFacilitiesTable = ({
  creditFacilities,
}: CreditFacilityTableProps) => {
  return (
    <Box mb="36px">
      <Heading size="md" mb="24px">
        Credit Facilities
      </Heading>
      <Table>
        <Thead>
          <Tr>
            <Th>Id</Th>
            <Th>Name</Th>
            <Th>Slug</Th>
            <Th>Borrower Entity Name</Th>
          </Tr>
        </Thead>
        <Tbody>
          {creditFacilities.map((creditFacility) => (
            <Tr key={creditFacility.id}>
              <Td color={colors.blue[400]}>
                <Link to={`facilities/${creditFacility.id}`}>
                  {creditFacility.id}
                </Link>
              </Td>
              <Td>{creditFacility.name}</Td>
              <Td>{creditFacility.slug}</Td>
              <Td>{creditFacility.borrowerEntityName}</Td>
              <Td />
            </Tr>
          ))}
        </Tbody>
      </Table>
      {creditFacilities.length === 0 && (
        <Alert> No credit facilities are defined.</Alert>
      )}
    </Box>
  );
};

// Exports
export const ImplementationDetails = () => {
  const params = useParams();
  const { implementationId } = RouteParamsStruct.parse(params);

  const query = useImplementationDetailsQuery({
    variables: { implementationId },
  });
  if (query.error) throw query.error;
  if (!query.data) return <Skeleton h="600px" />;

  const implementation = query.data.implementation;

  return (
    <>
      <Heading size="lg" mb="24px">
        {implementation.name}
      </Heading>
      <LoanProductsTable loanProducts={implementation.loanProducts} />
      <CreditFacilitiesTable
        creditFacilities={implementation.creditFacilities}
      />
    </>
  );
};
